import type {ChangeEvent} from 'react';

/**
 * A custom hook for managing the state of a checkbox input in a tag selection form.
 * This hook determines whether a checkbox is active based on the provided form value
 * and provides a handler for updating the state when the checkbox is toggled.
 *
 * @param formValue  The current value(s) of the form field, which can be a single string or an array of strings.
 * @param fieldValue The value associated with the checkbox input.
 * @param onChange   A callback function invoked when the checkbox state changes. It receives the updated value and checked status.
 * @returns An object containing:
 * - `handleChange`: A function to handle checkbox state changes.
 * - `isActive`: A boolean indicating whether the checkbox is checked.
 *
 * @example
 * ```tsx
 * const { handleChange, isActive } = useTagCheckbox(["tag1", "tag2"], "tag1", (value, isChecked) => {
 *   console.log(value, isChecked);
 * });
 *
 * <input type="checkbox" checked={isActive} onChange={handleChange} />
 * ```
 */
export const useTagCheckbox = (
    formValue: string[] | string,
    fieldValue: string,
    onChange: (value: string[] | string, isChecked: boolean) => void
) => {
    const isActive = Array.isArray(formValue) ? formValue.includes(fieldValue) : formValue === fieldValue;

    /**
     * Handles changes to the checkbox field by invoking the `onChange` callback with the new input value.
     * This function extracts the value from the event and passes it to the parent component or handler.
     *
     * @param e The change event from the input field.
     */
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value, e.target.checked);
    };

    return {
        handleChange,
        isActive
    };
};